/**
 * A drone, and the five minutes it has.
 *
 * The player takes it out, sends it up, and looks through it. What they get
 * is sight; what it costs them is that their own body is standing wherever
 * they left it, with the world still running around it. That is the whole
 * trade and it is the reason this is worth having: scouting the кремль before
 * you sail is a real advantage, and standing in a field with your eyes
 * somewhere else is a real price.
 *
 * **It collides.** The obvious way to build this is the fly mode that already
 * exists - no gravity, no collision - and it would be wrong: a drone that
 * passes through the fortress wall makes the fortress a picture of a
 * fortress. It has no weight and it does have walls.
 *
 * **It cannot leave the map.** The range is the whole world for now, which is
 * to say the edges are the range. A drone that flew out over the void and
 * kept going would be lost with no way to tell what had happened to it.
 *
 * Pure: arithmetic and a solid test. Nothing here knows about cameras.
 */

import { moveAABB, type SolidTest } from "../physics/voxelCollision.js";
import { LOOK_SENSITIVITY } from "../player/controller.js";

/** Seconds of flight in one battery. */
export const DRONE_FLIGHT = 300;

/**
 * Blocks a second, flat out.
 *
 * Faster than running - seven - because it is a machine and because the
 * point of it is to cross water you cannot cross. Slow enough that the far
 * island is a trip rather than a glance: about sixteen seconds from one edge
 * of this map to the other.
 */
export const DRONE_SPEED = 12;

/** How quickly it reaches that, and how quickly it stops. Per second. */
const DRONE_DAMPING = 6;

/** Small. It has to fit through the кремль's gate, which is three wide. */
export const DRONE_HALF_WIDTH = 0.2;
export const DRONE_HEIGHT = 0.25;

/** How far off the edges of the world it may go. */
const EDGE_MARGIN = 1;

/**
 * How many снаряди it carries, at most.
 *
 * Loaded before it leaves the ground and not a round more, which is what
 * makes the sortie a decision: six passes over the кремль, and what you did
 * not hit you come back for. Refilling in the air would make the drone a
 * siege engine that never has to land.
 */
export const DRONE_LOAD = 6;

/**
 * How far the ray under the crosshair looks for something to aim at.
 *
 * Far enough to cover a pass over the кремль from a comfortable height, and
 * short enough that pointing at the horizon does not ask for an arc nothing
 * could throw. Past it the bolt simply goes where it is pointing.
 */
export const DRONE_SIGHT = 96;

/**
 * Seconds between one release and the next.
 *
 * Without it a held button empties the rack in a single frame, six bolts land
 * in one square, and the whole load is spent on one hut.
 */
export const DROP_INTERVAL = 0.7;

export interface DroneInput {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  /** Up and down, which on a drone is throttle rather than jumping. */
  up: boolean;
  down: boolean;
}

export const NO_DRONE_INPUT: DroneInput = {
  forward: false, back: false, left: false, right: false, up: false, down: false,
};

/** The box the world is, so a drone cannot be flown out of it. */
export interface WorldBounds {
  readonly spanXZ: number;
  readonly high: number;
}

export class Drone {
  x = 0;
  y = 0;
  z = 0;
  yaw = 0;
  pitch = 0;
  vx = 0;
  vy = 0;
  vz = 0;
  /** Seconds of battery left. Nought means it is coming down. */
  charge = DRONE_FLIGHT;
  /** How many снаряди are still aboard. */
  load = 0;
  /** Seconds until the next one can go. */
  private reload = 0;

  /** True while there is power in it. */
  get flying(): boolean {
    return this.charge > 0;
  }

  /** Put it in the air at a point, looking the way somebody was looking. */
  launch(
    at: { x: number; y: number; z: number }, yaw: number, pitch: number,
  ): void {
    this.x = at.x; this.y = at.y; this.z = at.z;
    this.yaw = yaw; this.pitch = pitch;
    this.vx = 0; this.vy = 0; this.vz = 0;
    this.charge = DRONE_FLIGHT;
    this.reload = 0;
  }

  /** Where the camera goes. The same shape a player's eye has. */
  eyePosition(): { x: number; y: number; z: number } {
    return { x: this.x, y: this.y, z: this.z };
  }

  /**
   * Fly for `dt` seconds.
   *
   * Movement is relative to where it is pointing, flat: pitching the camera
   * down to look at the ground must not fly it into the ground, which is what
   * "forward means where I am looking" does to anybody trying to hold a
   * height and watch what is under them.
   */
  step(
    input: DroneInput, dt: number, isSolid: SolidTest, bounds: WorldBounds,
  ): void {
    if (this.charge > 0) this.charge = Math.max(0, this.charge - dt);
    if (this.reload > 0) this.reload = Math.max(0, this.reload - dt);

    const ahead = (input.forward ? 1 : 0) - (input.back ? 1 : 0);
    const side = (input.right ? 1 : 0) - (input.left ? 1 : 0);
    const lift = (input.up ? 1 : 0) - (input.down ? 1 : 0);

    // Yaw 0 faces -Z, the same as the player's.
    const sin = Math.sin(this.yaw);
    const cos = Math.cos(this.yaw);
    let wishX = -sin * ahead + cos * side;
    let wishZ = -cos * ahead - sin * side;
    const length = Math.hypot(wishX, wishZ);
    if (length > 1) { wishX /= length; wishZ /= length; }

    // Eased rather than set, so it glides a little when you let go - a drone
    // that stops dead the instant you release the key reads as a cursor.
    const t = 1 - Math.exp(-DRONE_DAMPING * dt);
    this.vx += (wishX * DRONE_SPEED - this.vx) * t;
    this.vz += (wishZ * DRONE_SPEED - this.vz) * t;
    this.vy += (lift * DRONE_SPEED - this.vy) * t;

    const moved = moveAABB(
      this.x, this.y, this.z, DRONE_HALF_WIDTH, DRONE_HEIGHT,
      this.vx * dt, this.vy * dt, this.vz * dt, isSolid,
    );
    this.x = moved.x; this.y = moved.y; this.z = moved.z;
    // A wall takes the speed with it, or the drone presses into the кремль
    // building up momentum that fires it away the moment it clears the edge.
    if (moved.hitX) this.vx = 0;
    if (moved.hitY) this.vy = 0;
    if (moved.hitZ) this.vz = 0;

    this.clampToWorld(bounds);
  }

  /**
   * Let one go, if there is one to let go of.
   *
   * Bookkeeping only - what a снаряд is and what it does when it lands is the
   * world's business, not this file's. What *is* this file's business is that
   * it falls from a moving thing: whoever makes it should give it the drone's
   * own speed, so hitting anything means leading it rather than hovering over
   * it and pressing.
   */
  dropOne(): boolean {
    if (this.load <= 0 || this.reload > 0 || !this.flying) return false;
    this.load--;
    this.reload = DROP_INTERVAL;
    return true;
  }

  /**
   * Turn, in the same units and the same direction the player turns.
   *
   * Which it said it did and did not: it took the raw pixel delta as radians
   * and added it, where the player scales by `LOOK_SENSITIVITY` and
   * subtracts. A hand's width of mouse spun the drone a dozen times over and
   * turned it the wrong way while doing it - "крутиться як скажений".
   */
  look(dx: number, dy: number, sensitivity = LOOK_SENSITIVITY): void {
    this.yaw -= dx * sensitivity;
    this.pitch = Math.max(-Math.PI / 2 + 0.01,
      Math.min(Math.PI / 2 - 0.01, this.pitch - dy * sensitivity));
  }

  /** Where it is pointing, the way a player's eye answers the same question. */
  lookDirection(): { x: number; y: number; z: number } {
    const cosPitch = Math.cos(this.pitch);
    return {
      x: -Math.sin(this.yaw) * cosPitch,
      y: Math.sin(this.pitch),
      z: -Math.cos(this.yaw) * cosPitch,
    };
  }

  private clampToWorld(bounds: WorldBounds): void {
    const low = EDGE_MARGIN;
    const highXZ = bounds.spanXZ - EDGE_MARGIN;
    if (this.x < low) { this.x = low; this.vx = 0; }
    if (this.x > highXZ) { this.x = highXZ; this.vx = 0; }
    if (this.z < low) { this.z = low; this.vz = 0; }
    if (this.z > highXZ) { this.z = highXZ; this.vz = 0; }
    if (this.y < low) { this.y = low; this.vy = 0; }
    if (this.y > bounds.high - EDGE_MARGIN) {
      this.y = bounds.high - EDGE_MARGIN;
      this.vy = 0;
    }
  }
}
